/**
 * Qué negocio tiene abierto el almacén: su rubro y sus módulos.
 *
 * Llegan en el ámbito de la sesión, igual que al POS (`scope.businessType` y
 * `scope.modules`, en la entrada y en cada renovación). El almacén web los
 * ignoraba: una farmacia que lo abría veía «Producción interna», «Recetas y
 * costeo» y «food cost», que no son de su negocio y la mandaban a pantallas
 * vacías.
 *
 * Mismo reparto que en el POS (`docs/34-pos-multirubro.md` §5): **qué se
 * enseña lo deciden los módulos**, y el rubro solo elige las palabras
 * (`vocabulario.ts`). Aquí nunca se pregunta «¿es un restaurante?» para
 * esconder algo.
 */

import { BUSINESS_TYPES, type BusinessType, type TenantModule } from './contracts.g';

/** Los rubros que conoce el servidor: salen del contrato (`contracts.g.ts`). */
export type Rubro = BusinessType;

const RUBROS: readonly Rubro[] = BUSINESS_TYPES;

/**
 * Los módulos que son del restaurante y que otro rubro puede no tener.
 *
 * Los mismos que `restaurantModules` del POS
 * (`frontend/lib/features/auth/domain/session.dart`). Con el tipo del contrato,
 * uno mal escrito no compila.
 */
export const MODULOS_DE_RESTAURANTE: readonly string[] = [
  'dining',
  'kitchen',
  'delivery',
  'reservations',
  'public_menu',
  'tips',
  'recipes',
] satisfies TenantModule[];

export interface Negocio {
  rubro: Rubro;
  modulos: ReadonlySet<string>;
}

/** El negocio de siempre: un restaurante con todo lo suyo. */
export const RESTAURANTE_CON_TODO: Negocio = {
  rubro: 'restaurant',
  modulos: new Set(MODULOS_DE_RESTAURANTE),
};

/**
 * El negocio a partir de lo que mandó el servidor.
 *
 * Sin rubro, o con uno que este almacén no conoce, es un restaurante: hasta la
 * fase 1 todos lo eran, y un servidor más nuevo puede mandar un rubro que aquí
 * todavía no existe.
 *
 * **Un servidor viejo no manda los módulos**, y sus locales son todos
 * restaurantes con todo. Por la misma razón que el POS, si es restaurante y no
 * llega ninguno de los de restaurante, se dan por encendidos: sin esto, el
 * chifa que abre el almacén contra un servidor sin desplegar perdía Recetas.
 * El precio es el mismo que paga el POS: un restaurante no puede apagar todos
 * los suyos a la vez, y no es un negocio que exista.
 */
export function negocioDe(scope: { businessType?: unknown; modules?: unknown }): Negocio {
  const rubro = RUBROS.includes(scope.businessType as Rubro)
    ? (scope.businessType as Rubro)
    : 'restaurant';

  const llegados = Array.isArray(scope.modules)
    ? scope.modules.filter((modulo): modulo is string => typeof modulo === 'string')
    : [];

  const modulos = new Set(llegados);

  if (rubro === 'restaurant' && !llegados.some((modulo) => MODULOS_DE_RESTAURANTE.includes(modulo))) {
    for (const modulo of MODULOS_DE_RESTAURANTE) modulos.add(modulo);
  }

  return { rubro, modulos };
}

export function tieneModulo(negocio: Negocio, modulo: string): boolean {
  return negocio.modulos.has(modulo);
}

/**
 * ¿Este negocio trabaja con recetas? Recetas, producción interna y costo real
 * cuelgan de aquí.
 *
 * **Respaldo mientras el servidor no mande `recipes`:** el módulo sale de
 * `kitchen` en la fase 6, y la migración que lo crea se lo enciende a todos los
 * que ya tienen cocina. Hasta que ese servidor esté desplegado, la sesión trae
 * `kitchen` y no `recipes`, y sin el respaldo el chifa perdía sus recetas el
 * día que se publicara este almacén. Cuando todos los servidores manden
 * `recipes`, el respaldo sobra y se quita: entonces una bodega que encienda la
 * cocina ya no verá Recetas sin pedirlas.
 */
export function tieneRecetas(negocio: Negocio): boolean {
  return tieneModulo(negocio, 'recipes') || tieneModulo(negocio, 'kitchen');
}
